import React, { useState } from 'react';
import { Button, Typography, useToast } from '@crisellevaldez/speculo-ui';
import { CheckCircle, AlertCircle, Database } from 'lucide-react';
import { Organization } from '../../../../types/organization.types';
import { getFubErrorMessage } from './utils/errorHelpers';

// Define custom field interface
interface FubCustomField {
  id: number;
  name: string;
  type: string;
  key: string;
}

// Define validation result interface
interface ValidationResult {
  missing_fields: string[];
  existing_fields: string[];
  all_fields_present: boolean;
}

interface FubCustomFieldsSetupProps {
  selectedOrg?: Organization;
  isConfigured: boolean;
  hasCustomFields: boolean;
  onCustomFieldsSetup: () => void;
  setupFubCustomFields: (orgId: string) => Promise<any>;
  getFubCustomFields: (orgId: string) => Promise<any>;
  validateFubCustomFields: (orgId: string) => Promise<any>;
  isSettingUpCustomFields: boolean;
  isFetchingCustomFields: boolean;
  isValidatingCustomFields: boolean;
}

export const FubCustomFieldsSetup: React.FC<FubCustomFieldsSetupProps> = ({
  selectedOrg,
  isConfigured,
  hasCustomFields,
  onCustomFieldsSetup,
  setupFubCustomFields,
  getFubCustomFields,
  validateFubCustomFields,
  isSettingUpCustomFields,
  isFetchingCustomFields,
  isValidatingCustomFields,
}) => {
  const [customFields, setCustomFields] = useState<FubCustomField[]>([]);
  const [customFieldsValidation, setCustomFieldsValidation] = useState<ValidationResult | null>(
    null
  );
  const { showToast } = useToast();

  // Load custom fields data
  const loadCustomFieldsData = async () => {
    if (!selectedOrg?.id) return;

    try {
      // Get custom fields
      const fieldsResult = await getFubCustomFields(selectedOrg.id);
      if (fieldsResult?.success) {
        setCustomFields(fieldsResult.custom_fields);
      }

      // Validate custom fields
      const validationResult = await validateFubCustomFields(selectedOrg.id);
      if (validationResult?.success) {
        setCustomFieldsValidation(validationResult.validation);
      }
    } catch (error) {
      console.error('Error loading custom fields data:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to load custom fields data',
        variant: 'error',
      });
    }
  };

  // Function to setup custom fields
  const handleSetupCustomFields = async () => {
    if (!selectedOrg?.id) return;

    try {
      const result = await setupFubCustomFields(selectedOrg.id);
      if (result?.success) {
        showToast({
          message: 'Custom fields setup completed successfully',
          variant: 'success',
        });

        // Refresh data
        await loadCustomFieldsData();

        // Notify parent component
        onCustomFieldsSetup();
      } else {
        showToast({
          message: getFubErrorMessage(result, 'Failed to setup custom fields'),
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error setting up custom fields:', error);
      showToast({
        message: getFubErrorMessage(error, 'Failed to setup custom fields'),
        variant: 'error',
      });
    }
  };

  // Function to validate custom fields
  const handleValidateCustomFields = async () => {
    if (!selectedOrg?.id) return;

    try {
      // Load custom fields data
      await loadCustomFieldsData();
      showToast({
        message: 'Custom fields validated successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error validating custom fields:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to validate custom fields',
        variant: 'error',
      });
    }
  };

  // If API key is not configured, don't show this component
  if (!isConfigured) {
    return null;
  }

  return (
    <div>
      <div className="space-y-4">
        <Typography variant="p">
          Speculo requires specific custom fields in Follow Up Boss to track AI engagement data.
        </Typography>

        {customFields.length > 0 ||
          (customFieldsValidation && (
            <>
              {/* Custom Fields Validation */}
              {customFieldsValidation && (
                <div
                  className={`p-3 rounded-md ${
                    customFieldsValidation.all_fields_present
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                  } mb-4`}
                >
                  <div className="flex items-center gap-2">
                    {customFieldsValidation.all_fields_present ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                    )}
                    <span className="text-sm">
                      {customFieldsValidation.all_fields_present
                        ? 'All required custom fields are present.'
                        : `Missing ${
                            customFieldsValidation.missing_fields?.length || 0
                          } required custom fields: ${customFieldsValidation.missing_fields?.join(', ')}`}
                    </span>
                  </div>
                </div>
              )}
            </>
          ))}

        {/* Setup and Validate Buttons */}
        <div className="flex justify-end gap-2">
          {hasCustomFields && (
            <Button
              variant="outline"
              type="button"
              onClick={handleValidateCustomFields}
              disabled={isFetchingCustomFields || isValidatingCustomFields}
              isLoading={isFetchingCustomFields || isValidatingCustomFields}
              className="flex items-center gap-1 h-8 bg-green-50 !bg-[#1c994a] !text-white"
            >
              <CheckCircle className="h-4 w-4" />
              Validate Fields
            </Button>
          )}
          <Button
            variant="outline"
            type="button"
            onClick={handleSetupCustomFields}
            disabled={isSettingUpCustomFields}
            isLoading={isSettingUpCustomFields}
            className="flex items-center gap-1 h-8"
          >
            <Database className="h-4 w-4" />
            Setup Custom Fields
          </Button>
        </div>
      </div>
    </div>
  );
};
